import { ReadonlyVec3, vec3 } from "gl-matrix";

import { degToRad } from "utils/math";
import { isRealVec } from "utils/misc";

export function getSurfaceNormal(normal: ReadonlyVec3 | undefined): vec3 | undefined {
    if (!normal) {
        return undefined;
    }

    const candidate = vec3.fromValues(normal[0] ?? NaN, normal[1] ?? NaN, normal[2] ?? NaN);
    if (!isRealVec([candidate[0], candidate[1], candidate[2]]) || vec3.dot(candidate, candidate) === 0) {
        return undefined;
    }

    return vec3.normalize(candidate, candidate);
}

export function getPlaneCreationNormal(normal: ReadonlyVec3 | undefined): vec3 {
    return getSurfaceNormal(normal) ?? vec3.fromValues(0, 0, 1);
}

/** Cuts within this angle of perfectly horizontal/vertical snap to exact 0°/90°. */
export const AXIS_SNAP_TOLERANCE_DEG = 0.5;

const cosTolerance = Math.cos(degToRad(AXIS_SNAP_TOLERANCE_DEG)); // near-vertical normal
const sinTolerance = Math.sin(degToRad(AXIS_SNAP_TOLERANCE_DEG)); // near-horizontal normal

export function snapSectionPlaneNormalToAxis(normal: vec3): vec3 {
    const nz = normal[2];
    const absNz = Math.abs(nz);
    if (absNz >= cosTolerance) {
        // normal ~parallel to Z → perfectly horizontal cut
        return vec3.fromValues(0, 0, nz < 0 ? -1 : 1);
    }
    if (absNz <= sinTolerance) {
        // normal ~perpendicular to Z → perfectly vertical cut; keep azimuth, drop tilt
        const snapped = vec3.fromValues(normal[0], normal[1], 0);
        return vec3.normalize(snapped, snapped);
    }
    return normal;
}
